import { Link } from '@tanstack/react-router';

import { useCopy } from '../../shared/lib/use-copy';
import { Button } from '../../shared/ui/button';
import { copyText, errorDetails } from './lib';

interface IScreenErrorProps {
  error: unknown;
  /** What the second action does; a crashed panel closes instead of leaving for Flows. */
  onClose?: { label: string; run: () => void };
  compact?: boolean;
}

// Router's own screen printed a raw hook message with "Hide Error": this one says what
// happened in words and offers a way out, and the sidebar around it keeps working.
export const ScreenError = ({ error, onClose, compact }: IScreenErrorProps) => {
  const { copied, copy } = useCopy();
  return (
    <div
      role="alert"
      className={
        compact
          ? 'flex flex-col gap-4 p-card'
          : 'mx-auto flex w-full max-w-page-narrow flex-col gap-4 p-page pt-20'
      }
    >
      <h1 className="text-page font-semibold text-primary">
        {compact ? 'This panel stopped working' : 'This screen stopped working'}
      </h1>
      <p className="max-w-prose-line text-body text-muted">
        {compact
          ? 'The rest of the editor still works. Close the panel, or reload to try again.'
          : 'Nothing you saved is lost. Reload to try again; if it happens again, copy the details and send them to us.'}
      </p>
      <details className="rounded-control border border-border bg-surface-sunken px-input py-3 text-meta text-muted">
        <summary className="cursor-pointer text-data text-primary">Details</summary>
        <pre className="mt-3 max-h-error-details overflow-auto font-mono text-micro whitespace-pre-wrap text-muted">
          {errorDetails(error)}
        </pre>
      </details>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => window.location.reload()}>
          Reload
        </Button>
        {onClose ? (
          <Button size="sm" variant="secondary" onClick={onClose.run}>
            {onClose.label}
          </Button>
        ) : (
          <Link
            to="/flows"
            className="inline-flex h-control-sm items-center rounded-control border border-border px-input text-data text-primary hover:bg-hover"
          >
            Go to Flows
          </Link>
        )}
        <Button size="sm" variant="ghost" onClick={() => void copy(copyText(error))}>
          {copied ? 'Copied' : 'Copy details'}
        </Button>
      </div>
    </div>
  );
};

export const ScreenNotFound = () => (
  <div className="mx-auto flex w-full max-w-page-narrow flex-col gap-4 p-page pt-20">
    <h1 className="text-page font-semibold text-primary">Nothing lives at this address</h1>
    <p className="max-w-prose-line text-body text-muted">
      The link may be old, or what it pointed at was deleted.
    </p>
    <div>
      <Link
        to="/flows"
        className="inline-flex h-control-sm items-center rounded-control bg-accent px-input text-data font-semibold text-surface"
      >
        Go to Flows
      </Link>
    </div>
  </div>
);
